import React, { useEffect, useMemo, useState } from 'react';
import {
  BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs,
  TextInput, fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import {
  EMPTY_TAG_FILTER, LIFECYCLE_LABEL, TagFilter, TagFilterValue, UserTagCells, tagFilterParams, tagText,
  toLifecycle,
} from './tags/shared';

/**
 * Chat Statistics group — admin_panel_v2 Tab 18 (Landing Page Chats), the
 * largest single view in the legacy panel. One filter bar drives every
 * sub-tab; the server derives all of it from one memoised session list, so
 * "Refresh data" (POST chat/refresh) is what busts that memo.
 *
 * Two contracts from the backend that shape the code here:
 *  - exclude_emails ABSENT means "apply the built-in exclusion list", while
 *    present-but-empty means "exclude nobody". We prefill from the server's
 *    default_exclude_emails and always send the key, so what the operator sees
 *    in the filter bar is exactly what the numbers used.
 *  - the Excluded sub-tab honours only the date/hour filters, by design.
 */

type Filters = {
  user_type: string; prompt_type: string; results: string;
  start_date: string; end_date: string;
  exclude_emails: string[]; exclude_dates: string[]; exclude_hours: string[];
};

type Note = { kind: 'error' | 'ok'; text: string };

const TABS = ['Overview', 'Monthly', 'Daily & hourly', 'Returning users',
  'Sessions', 'Excluded', 'Prompt cards'];

const num = (v: any) => (typeof v === 'number' ? v.toLocaleString() : (v ?? '—'));

/** The account's lifecycle + tags as text (user_tags.decorate, by e-mail;
 *  anonymous Cosmos Mini sessions have none) — what the table's own search,
 *  column filter and CSV see; the cell renders pills. */
const lifecycleText = (r: any) => {
  const lc = toLifecycle(r?.lifecycle);
  return [r?.lifecycle_label || LIFECYCLE_LABEL[lc] || lc, tagText(r)].filter(Boolean).join(' · ');
};
/** Rows with the text column added; the renderer reads the pills off the same row. */
const withLifecycleText = (rows: any[]) => rows.map(r => ({ ...r, lifecycle_tags: lifecycleText(r) }));
const LIFECYCLE_RENDER = { lifecycle_tags: (r: Record<string, any>) => (r.email ? <span className="inline-block min-w-[12rem]"><UserTagCells row={r} /></span> : '') };

/** loaded_at / refresh timestamps are UTC ISO strings; label them as such
 *  rather than letting the browser re-interpret them as local time. */
const utc = (v: any) => (v ? `${String(v).replace('T', ' ').slice(0, 19)} UTC` : '—');

const downloadRows = (rows: any[], cols: string[], name: string) => {
  const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.map(esc).join(',')]
    .concat(rows.map(r => cols.map(c => esc(r[c])).join(',')))
    .join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

// ---------------------------------------------------------------------------
const MultiPick: React.FC<{
  label: string; options: { value: string; label: string }[];
  selected: string[]; onChange: (v: string[]) => void; searchable?: boolean;
}> = ({ label, options, selected, onChange, searchable }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const view = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? options.filter(o => o.label.toLowerCase().includes(n)) : options;
  }, [options, q]);
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1200 whitespace-nowrap font-grotesk">
        {label}: <b className="text-grayscale-1300">{selected.length || 'none'}</b> ▾
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-80 max-h-96 overflow-auto bg-white rounded-xl border border-grayscale-300 p-3 flex flex-col gap-2 shadow-lg">
          <div className="flex items-center gap-2">
            <Btn small onClick={() => onChange(options.map(o => o.value))}>All</Btn>
            <Btn small onClick={() => onChange([])}>None</Btn>
            <span className="text-xs text-grayscale-1100 flex-1">{selected.length} selected</span>
            <Btn small kind="primary" onClick={() => setOpen(false)}>Done</Btn>
          </div>
          {searchable && <TextInput value={q} onChange={setQ} placeholder="Search…" wide />}
          <div className="flex flex-col gap-0.5">
            {view.map(o => (
              <label key={o.value}
                className="flex items-center gap-2 text-sm text-grayscale-1300 px-1 py-0.5 rounded hover:bg-grayscale-100 cursor-pointer">
                <input type="checkbox" checked={selected.includes(o.value)}
                  onChange={() => toggle(o.value)} />
                <span className="truncate" title={o.label}>{o.label}</span>
              </label>
            ))}
            {!view.length && <span className="text-xs text-grayscale-1100 px-1">No matches.</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-grayscale-1100">{children}</p>
);

// ---------------------------------------------------------------------------
const ChatStatsPanel: React.FC = () => {
  const [tab, setTab] = useState(TABS[0]);
  const [nonce, setNonce] = useState(0);
  const [f, setF] = useState<Filters | null>(null);
  // The account's lifecycle + tags (standard params). Every sub-tab but
  // Excluded reads the same filtered session list on the server; anonymous
  // Cosmos Mini sessions have no account and never match a set filter.
  const [tagF, setTagF] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const [msg, setMsg] = useState<Note | null>(null);
  const [busy, setBusy] = useState(false);

  const opts = useAdminGet('chat', 'filters', {}, [nonce]);

  // The filter bar is only meaningful once the option lists exist; seeding
  // exclude_emails from the server's defaults keeps the first render's numbers
  // identical to the legacy pre-selected list.
  useEffect(() => {
    if (opts.data && !f) {
      setF({
        user_type: 'All', prompt_type: 'All', results: 'All',
        start_date: '', end_date: '',
        exclude_emails: opts.data.default_exclude_emails || [],
        exclude_dates: [], exclude_hours: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.data]);

  const params = useMemo(() => f && ({
    user_type: f.user_type, prompt_type: f.prompt_type, results: f.results,
    start_date: f.start_date, end_date: f.end_date,
    exclude_emails: f.exclude_emails.join(','),
    exclude_dates: f.exclude_dates.join(','),
    exclude_hours: f.exclude_hours.join(','),
    ...tagFilterParams(tagF),
  }), [f, tagF]);
  const dateParams = useMemo(() => f && ({
    start_date: f.start_date, end_date: f.end_date,
    exclude_dates: f.exclude_dates.join(','),
    exclude_hours: f.exclude_hours.join(','),
  }), [f]);

  const qkey = `${JSON.stringify(params)}|${nonce}`;
  const dkey = `${JSON.stringify(dateParams)}|${nonce}`;

  const refresh = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost('chat', 'refresh', {});
    setBusy(false);
    if (error || !data?.success) {
      setMsg({ kind: 'error', text: error || 'Refresh failed' });
      return;
    }
    const caps = (data.caps_hit || []).length ? ` Caps hit: ${data.caps_hit.join(', ')}.` : '';
    setMsg({ kind: 'ok', text: `Rebuilt ${num(data.sessions)} sessions at ${utc(data.loaded_at)}.${caps}` });
    setNonce(n => n + 1);   // every sub-tab query keys off this
  };

  const reset = () => { setTagF(EMPTY_TAG_FILTER); setF({
    user_type: 'All', prompt_type: 'All', results: 'All',
    start_date: '', end_date: '',
    exclude_emails: opts.data?.default_exclude_emails || [],
    exclude_dates: [], exclude_hours: [],
  }); };

  const o = opts.data;
  const capsHit: string[] = o?.caps_hit || [];

  return (
    <div className="flex flex-col gap-4">
      <Section title="Landing page chats"
        right={
          <div className="flex items-center gap-2">
            <span className="text-xs text-grayscale-1100">
              {o ? `${num(o.total_sessions)} sessions · built ${utc(o.loaded_at)}` : ''}
            </span>
            <Btn small disabled={busy} onClick={refresh}>{busy ? 'Rebuilding…' : 'Refresh data'}</Btn>
          </div>
        }>
        {o?.intro && <Caption>{o.intro}</Caption>}
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {opts.error && <Msg kind="error">{opts.error}</Msg>}
        {capsHit.length > 0 && (
          <Msg kind="error">
            Row caps hit while building ({capsHit.join(', ')}) — these statistics are
            computed from a truncated slice of the tables.
          </Msg>
        )}
        {opts.loading && !o ? <Loading label="Building the session list (first load reads the full chat history)…" /> : null}
        {!opts.loading && !o && <Btn small onClick={opts.reload}>Retry</Btn>}
        {f && o && (
          <>
            <div className="flex gap-2 flex-wrap items-center">
              <Select value={f.user_type} onChange={v => setF({ ...f, user_type: v })}
                options={o.user_types || ['All']} />
              <Select value={f.prompt_type} onChange={v => setF({ ...f, prompt_type: v })}
                options={o.prompt_types || ['All']} />
              <Select value={f.results} onChange={v => setF({ ...f, results: v })}
                options={o.results_options || ['All']} />
              <label className="flex items-center gap-1 text-xs text-grayscale-1100">
                From
                <TextInput type="date" value={f.start_date} onChange={v => setF({ ...f, start_date: v })} />
              </label>
              <label className="flex items-center gap-1 text-xs text-grayscale-1100">
                To
                <TextInput type="date" value={f.end_date} onChange={v => setF({ ...f, end_date: v })} />
              </label>
              <MultiPick label="Excluded emails" searchable
                options={(o.emails || []).map((e: string) => ({ value: e, label: e }))}
                selected={f.exclude_emails} onChange={v => setF({ ...f, exclude_emails: v })} />
              <MultiPick label="Excluded days" searchable
                options={(o.dates || []).map((d: string) => ({ value: d, label: d }))}
                selected={f.exclude_dates} onChange={v => setF({ ...f, exclude_dates: v })} />
              <MultiPick label="Excluded hours"
                options={(o.hours || []).map((h: any) => ({ value: String(h.value), label: h.label }))}
                selected={f.exclude_hours} onChange={v => setF({ ...f, exclude_hours: v })} />
              <Btn small onClick={reset}>Reset filters</Btn>
            </div>
            <TagFilter value={tagF} onChange={v => setTagF(v)} />
            {!f.exclude_emails.length && (
              <Caption>
                No emails excluded — internal and test traffic is counted as real usage
                in every number below. Reset filters restores the default exclusion list.
              </Caption>
            )}
          </>
        )}
      </Section>

      <SubTabs tabs={TABS} active={tab} onChange={setTab} />

      {params && dateParams && (
        <>
          {tab === 'Overview' && <Overview params={params} qkey={qkey} />}
          {tab === 'Monthly' && <Monthly params={params} qkey={qkey} />}
          {tab === 'Daily & hourly' && <DailyHourly params={params} qkey={qkey} />}
          {tab === 'Returning users' && <Returning params={params} qkey={qkey} />}
          {tab === 'Sessions' && <Sessions params={params} qkey={qkey} />}
          {tab === 'Excluded' && <Excluded params={dateParams} qkey={dkey} />}
        </>
      )}
      {tab === 'Prompt cards' && <PromptCards />}
    </div>
  );
};

type TabProps = { params: Record<string, string>; qkey: string };

// ---------------------------------------------------------------------------
const Overview: React.FC<TabProps> = ({ params, qkey }) => {
  const q = useAdminGet('chat', 'overview', params, [qkey]);
  const d: any = q.data;
  const k: any = d?.kpis || {};
  const byType: [string, any][] = Object.entries(d?.by_prompt_type || {});

  return (
    <div className="flex flex-col gap-4">
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Total chats" value={num(k.total_chats)}
              hint={`${num(k.total_messages)} messages`} />
            <KPI label="Cosmos (logged-in)" value={num(k.cosmos)} />
            <KPI label="Cosmos Mini (anon)" value={num(k.cosmos_mini)} />
            <KPI label="With results" value={num(k.with_results)} />
            <KPI label="% with results" value={`${k.pct_with_results ?? 0}%`} />
            <KPI label="Avg conversation" value={k.avg_conv_length ?? 0} hint="messages per chat" />
            <KPI label="Avg relevance" value={k.avg_relevance ?? 0} hint="mixed 0–1 / 0–100 scale" />
            <KPI label="Blank sessions" value={num(k.blank_sessions)} hint="0 messages, excluded" />
          </KPIRow>
          <Caption>{d.note} Built {utc(d.loaded_at)}.</Caption>
          <Section title="Prompt type mix">
            <BarList items={byType.map(([label, value]) => ({ label, value: Number(value) || 0 }))}
              total={k.total_chats || undefined} />
          </Section>
          <Section title={`Blank sessions (${num(k.blank_sessions)})`}>
            <Caption>
              Sessions that were opened but produced no messages. They are stripped out
              of every metric, table and chart on this page — which is why the KPI total
              is lower than the raw session count.
            </Caption>
            {d.blank_sessions_truncated && (
              <Msg kind="error">Only the first {(d.blank_sessions || []).length} blank sessions are listed.</Msg>
            )}
            <DataTable rows={d.blank_sessions || []} csvName="chat_blank_sessions" searchable
              columns={['session_id', 'user_type', 'created_at']}
              empty="No blank sessions in this slice." maxHeight="20rem" />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const MONTH_COLS = ['month', 'total_chats', 'cosmos', 'cosmos_mini', 'unique_cosmos_users',
  'prompt_cards', 'custom_queries', 'pct_custom', 'with_results', 'no_results',
  'pct_with_results', 'followup_only_no_results', 'avg_relevance'];

const MONTH_LABELS: Record<string, string> = {
  total_chats: 'Total chats', cosmos_mini: 'Cosmos Mini',
  unique_cosmos_users: 'Unique Cosmos users', prompt_cards: 'Prompt cards',
  custom_queries: 'Custom queries', pct_custom: '% custom',
  with_results: 'With results', no_results: 'No results',
  pct_with_results: '% with results',
  followup_only_no_results: 'Follow-up only, no results', avg_relevance: 'Avg relevance',
};

const COMPARISONS: Record<string, [string, string]> = {
  'Cosmos vs Cosmos Mini': ['cosmos', 'cosmos_mini'],
  'Prompt cards vs Custom queries': ['prompt_cards', 'custom_queries'],
  'Results shown vs No results': ['with_results', 'no_results'],
};

const Monthly: React.FC<TabProps> = ({ params, qkey }) => {
  const q = useAdminGet('chat', 'monthly', params, [qkey]);
  const [cmp, setCmp] = useState(Object.keys(COMPARISONS)[0]);
  const d: any = q.data;
  const rows: any[] = d?.rows || [];
  const [a, b] = COMPARISONS[cmp];

  return (
    <div className="flex flex-col gap-4">
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading ? <Loading /> : (
        <>
          <Section title="Month-wise chat summary">
            {d?.note && <Caption>{d.note}</Caption>}
            <DataTable rows={rows} columns={MONTH_COLS} labels={MONTH_LABELS}
              csvName="chat_monthly_summary" empty="No months in this slice." maxHeight="32rem" />
          </Section>
          <Section title="Monthly series"
            right={<Select value={cmp} onChange={setCmp} options={Object.keys(COMPARISONS)} />}>
            {rows.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[a, b].map(metric => (
                  <div key={metric} className="flex flex-col gap-2">
                    <div className="text-xs uppercase tracking-wide text-grayscale-1100">
                      {MONTH_LABELS[metric] || metric.replace(/_/g, ' ')}
                    </div>
                    <BarList items={rows.map(r => ({ label: r.month, value: Number(r[metric]) || 0 }))} />
                  </div>
                ))}
              </div>
            ) : <Caption>Nothing to chart.</Caption>}
          </Section>
          <Section title="Average relevance by month">
            <Caption>
              Relevance mixes two storage scales — stored search results score 0–100,
              message attachments score 0–1 — so a month is comparable with another
              month only within the same storage tier. Months with no scored result
              are absent rather than zero.
            </Caption>
            <DataTable rows={d?.relevance_series || []} columns={['month', 'avg_relevance']}
              labels={{ avg_relevance: 'Avg relevance' }} csvName="chat_monthly_relevance"
              empty="No relevance scores in this slice." maxHeight="20rem" />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const DailyHourly: React.FC<TabProps> = ({ params, qkey }) => {
  const q = useAdminGet('chat', 'timeseries', params, [qkey]);
  const d: any = q.data;
  const daily: any[] = d?.daily || [];
  const hourly: any[] = d?.hourly || [];

  return (
    <div className="flex flex-col gap-4">
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Chats in slice" value={num(d.total_chats)} />
            <KPI label="Active days" value={num(daily.length)} />
            <KPI label="Busiest day" value={d.top_days?.[0]?.date || '—'}
              hint={d.top_days?.[0] ? `${num(d.top_days[0].chats)} chats` : undefined} />
            <KPI label="Busiest hour" value={d.top_hours?.[0]?.hour || '—'}
              hint={d.top_hours?.[0] ? `${num(d.top_hours[0].chats)} chats` : undefined} />
          </KPIRow>
          <Section title="Daily chats">
            <DataTable rows={daily} columns={['date', 'cosmos', 'cosmos_mini', 'total']}
              labels={{ cosmos_mini: 'Cosmos Mini' }} render={{ date: r => String(r.date) }}
              csvName="chat_daily" searchable empty="No days in this slice." maxHeight="26rem" />
          </Section>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Most active days (top 10)">
              <BarList items={(d.top_days || []).map((r: any) => ({ label: r.date, value: r.chats }))} />
              {!(d.top_days || []).length && <Caption>No days in this slice.</Caption>}
            </Section>
            <Section title="Most active hours (top 10)">
              <BarList items={(d.top_hours || []).map((r: any) => ({ label: r.hour, value: r.chats }))} />
              {!(d.top_hours || []).length && <Caption>No hours in this slice.</Caption>}
            </Section>
          </div>
          <Section title="Chats by hour of day">
            <Caption>{d.hours_note}</Caption>
            <BarList items={hourly.map((h: any) => ({ label: h.hour, value: h.chats }))} />
          </Section>
          <Section title="Prompt type distribution">
            <BarList items={(d.prompt_distribution || []).map((r: any) => ({ label: r.type, value: r.count }))}
              total={d.total_chats || undefined} />
            <DataTable rows={d.prompt_distribution || []} columns={['type', 'count', 'pct']}
              labels={{ pct: '% of chats' }} csvName="chat_prompt_distribution"
              empty="No chats in this slice." maxHeight="16rem" />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const Returning: React.FC<TabProps> = ({ params, qkey }) => {
  const [email, setEmail] = useState('');
  const [emailQ, setEmailQ] = useState('');
  const q = useAdminGet('chat', 'returning', { ...params, email }, [qkey, email]);
  const d: any = q.data;
  const byMonth: any[] = d?.by_month || [];
  const emails: string[] = d?.emails || [];
  const shown = useMemo(() => {
    const n = emailQ.trim().toLowerCase();
    return (n ? emails.filter(e => e.includes(n)) : emails).slice(0, 300);
  }, [emails, emailQ]);
  const detail: any = d?.user_detail;

  return (
    <div className="flex flex-col gap-4">
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading ? <Loading /> : d && (
        <>
          <Section title="Returning vs new users by month">
            <Caption>
              {d.definition} Only Cosmos (logged-in) sessions with a known email take
              part — anonymous Cosmos Mini sessions have no identity to return with.
            </Caption>
            <DataTable rows={byMonth} columns={['month', 'new_users', 'returning_users', 'total_unique']}
              labels={{ new_users: 'New', returning_users: 'Returning', total_unique: 'Total unique' }}
              csvName="chat_returning_by_month" empty="No logged-in activity in this slice." />
            {byMonth.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="flex flex-col gap-2">
                  <div className="text-xs uppercase tracking-wide text-grayscale-1100">New users</div>
                  <BarList items={byMonth.map(r => ({ label: r.month, value: r.new_users }))} />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="text-xs uppercase tracking-wide text-grayscale-1100">Returning users</div>
                  <BarList items={byMonth.map(r => ({ label: r.month, value: r.returning_users }))} />
                </div>
              </div>
            )}
          </Section>

          <Section title={`Users active in more than one month (${(d.multi_month_users || []).length})`}>
            <DataTable rows={withLifecycleText(d.multi_month_users || [])}
              columns={['email', 'lifecycle_tags', 'active_months', 'first', 'last']}
              labels={{ lifecycle_tags: 'Lifecycle · tags', active_months: 'Active months', first: 'First month', last: 'Last month' }}
              render={LIFECYCLE_RENDER}
              searchable csvName="chat_multi_month_users"
              empty="Nobody came back in a second month in this slice."
              actions={r => <Btn small onClick={() => setEmail(r.email)}>Drill down</Btn>} />
          </Section>

          <Section title="Per-user drill-down"
            right={email ? <Btn small onClick={() => setEmail('')}>Clear</Btn> : undefined}>
            <div className="flex gap-2 flex-wrap items-center">
              <TextInput value={emailQ} onChange={setEmailQ} placeholder="Search the email list…" />
              <Select value={email} onChange={setEmail}
                options={['', ...shown]} labels={{ '': `Pick a user (${emails.length} with activity)` }} />
              {emails.length > shown.length && !emailQ.trim() && (
                <span className="text-xs text-grayscale-1100">
                  showing first {shown.length} — search to narrow
                </span>
              )}
            </div>
            {email && !detail && <Caption>No activity for {email} under the current filters.</Caption>}
            {detail && (
              <>
                <KPIRow>
                  <KPI label="Email" value={<span className="text-sm">{detail.email}</span>} />
                  <KPI label="Sessions" value={num(detail.sessions)} />
                  <KPI label="First month" value={detail.first_month} />
                  <KPI label="Last month" value={detail.last_month} />
                </KPIRow>
                <DataTable rows={detail.months || []}
                  columns={['month', 'chats', 'messages', 'avg_length']}
                  labels={{ avg_length: 'Avg messages per chat' }}
                  csvName={`chat_user_${detail.email}`} empty="No monthly activity." />
              </>
            )}
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
/** Full export pulls every filtered row WITH transcripts — tens of MB is normal,
 *  so the operator has to acknowledge it before the request goes out. */
const FullExport: React.FC<{
  action: string; params: Record<string, string>; name: string; extra?: Record<string, string>;
}> = ({ action, params, name, extra }) => {
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Note | null>(null);

  const run = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelGet('chat', action, { ...params, ...(extra || {}), full: '1' });
    setBusy(false);
    if (error || !data) { setMsg({ kind: 'error', text: error || 'Export failed' }); return; }
    downloadRows(data.rows || [], data.columns || [], name);
    setMsg({
      kind: 'ok',
      text: `Exported ${num(data.returned)} of ${num(data.total)} rows.` +
        (data.truncated ? ' The server cap was hit — narrow the date range to get the rest.' : ''),
    });
  };

  return (
    <div className="flex flex-col gap-2 border-t border-grayscale-300 pt-3">
      <Caption>
        The full export includes every filtered row with its complete transcript
        (chat_history_json), which is what the legacy CSV contained. Transcripts carry
        the raw search-result payloads, so a few hundred sessions can run to tens of
        megabytes and the request takes a while.
      </Caption>
      <label className="flex items-center gap-2 text-xs text-grayscale-1200">
        <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
        I understand this downloads full transcripts and may be very large.
      </label>
      <div>
        <Btn kind="primary" disabled={!ack || busy} onClick={run}>
          {busy ? 'Building export…' : 'Download CSV with transcripts'}
        </Btn>
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
    </div>
  );
};

// ---------------------------------------------------------------------------
const SESSION_COLS = ['session_id', 'created_at', 'user_type', 'name', 'email', 'lifecycle_tags', 'prompt_type',
  'first_user_msg', 'results_count', 'avg_relevance', 'followup_gen', 'followup_ans',
  'user_post_results', 'message_count', 'results_history_text'];

const SESSION_LABELS: Record<string, string> = {
  lifecycle_tags: 'Lifecycle · tags',
  first_user_msg: 'First user message', results_count: 'Results',
  avg_relevance: 'Avg relevance', followup_gen: 'Follow-ups generated',
  followup_ans: 'Follow-ups answered', user_post_results: 'User msgs after results',
  message_count: 'Messages', results_history_text: 'Results shown',
};

const PAGE_SIZES = ['50', '100', '200', '500', '1000'];

const Sessions: React.FC<TabProps> = ({ params, qkey }) => {
  const [limit, setLimit] = useState('200');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => { setOffset(0); }, [qkey, limit]);

  const q = useAdminGet('chat', 'sessions', { ...params, limit, offset },
    [qkey, limit, offset]);
  const d: any = q.data;
  const rows: any[] = useMemo(() => withLifecycleText(d?.rows || []), [d]);
  const total = d?.total || 0;
  const size = Number(limit) || 200;
  const pages = Math.max(1, Math.ceil(total / size));
  const page = Math.floor(offset / size) + 1;

  return (
    <div className="flex flex-col gap-4">
      <Section title={`Session details (${num(total)} sessions)`}
        right={
          <div className="flex items-center gap-2">
            <span className="text-xs text-grayscale-1100">Rows per page</span>
            <Select value={limit} onChange={setLimit} options={PAGE_SIZES} />
            <Btn small onClick={q.reload}>Refresh</Btn>
          </div>
        }>
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading ? <Loading /> : (
          <>
            <DataTable rows={rows} columns={SESSION_COLS} labels={SESSION_LABELS} render={LIFECYCLE_RENDER}
              searchable csvName="chat_sessions"
              empty="No sessions match these filters."
              maxHeight="34rem"
              actions={r => <Btn small onClick={() => setOpen(String(r.session_id))}>Open</Btn>} />
            <Caption>
              The CSV button above exports the {rows.length} rows on this page without
              transcripts. Search filters the current page only — paging happens on the server.
            </Caption>
            <div className="flex items-center gap-2 justify-end text-xs text-grayscale-1100">
              <Btn small disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - size))}>Prev</Btn>
              Page {page} of {pages}
              <Btn small disabled={offset + size >= total} onClick={() => setOffset(o => o + size)}>Next</Btn>
            </div>
          </>
        )}
        <FullExport action="sessions" params={params} name="chat_sessions_full" />
      </Section>
      {open && <SessionDrawer sessionId={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const SessionDrawer: React.FC<{ sessionId: string; onClose: () => void }> =
  ({ sessionId, onClose }) => {
  const q = useAdminGet('chat', 'sessions', { session_id: sessionId }, [sessionId]);
  const r: any = q.data?.row;
  const history: any[] = r?.chat_history || [];

  const saveJson = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([r.chat_history_json || '[]'], { type: 'application/json' }));
    a.download = `chat_${sessionId}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-2xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">Session {sessionId}</h2>
            <div className="text-xs text-grayscale-1100">
              {r ? `${r.user_type} · ${fmtDate(r.created_at)}` : 'Loading…'}
            </div>
          </div>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading ? <Loading /> : r && (
          <>
            <Section title="Session">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-grayscale-1300">
                <div><span className="text-grayscale-1100">User: </span>{r.name || '—'} {r.email ? `(${r.email})` : ''}</div>
                <div><span className="text-grayscale-1100">Prompt type: </span>{r.prompt_type}</div>
                <div><span className="text-grayscale-1100">Messages: </span>{r.message_count}</div>
                <div><span className="text-grayscale-1100">Results: </span>{r.results_count}</div>
                <div><span className="text-grayscale-1100">Avg relevance: </span>{r.avg_relevance ?? '—'}</div>
                <div><span className="text-grayscale-1100">Follow-ups: </span>{r.followup_gen} generated / {r.followup_ans} answered</div>
                <div><span className="text-grayscale-1100">User msgs after results: </span>{r.user_post_results}</div>
                <div><span className="text-grayscale-1100">Hour: </span>
                  {r.hour === null || r.hour === undefined ? '—' : `${r.hour}:00`}</div>
              </div>
              <div className="text-sm text-grayscale-1300">
                <span className="text-grayscale-1100">First user message: </span>
                {r.first_user_msg || '—'}
              </div>
            </Section>
            <Section title={`Transcript (${history.length} messages)`}
              right={<Btn small onClick={saveJson}>Download JSON</Btn>}>
              {history.length ? (
                <div className="flex flex-col gap-2">
                  {history.map((m: any, i: number) => {
                    const role = typeof m === 'string' ? 'user'
                      : String(m?.role || m?.sender || '').toLowerCase() === 'user' ? 'user' : 'assistant';
                    const content = typeof m === 'string' ? m : (m?.content || m?.text || '');
                    const atts = (typeof m === 'object' && m?.attachments) || [];
                    return (
                      <div key={i} className={`rounded-lg px-3 py-2 text-sm border ${role === 'user'
                        ? 'bg-white border-grayscale-300 text-grayscale-1300'
                        : 'bg-grayscale-200 border-grayscale-300 text-grayscale-1200'}`}>
                        <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 flex gap-2">
                          <span>{role}</span>
                          {typeof m === 'object' && m?.timestamp && <span>{fmtDate(m.timestamp)}</span>}
                          {atts.length > 0 && <span>{atts.length} attachment(s)</span>}
                        </div>
                        <div className="whitespace-pre-wrap break-words">{String(content) || '—'}</div>
                      </div>
                    );
                  })}
                </div>
              ) : <Caption>No transcript stored for this session.</Caption>}
            </Section>
            <Section title={`Results shown (${(r.results_history || []).length})`}>
              <DataTable rows={r.results_history || []} columns={['title', 'country', 'score']}
                empty="No search results were stored for this session." maxHeight="20rem" />
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const EXCLUDED_COLS = ['session_id', 'created_at', 'email', 'name', 'prompt_type',
  'first_user_msg', 'results_count', 'avg_relevance', 'message_count', 'results_history_text'];

const Excluded: React.FC<TabProps> = ({ params, qkey }) => {
  const q = useAdminGet('chat', 'excluded', params, [qkey]);
  const list = useAdminGet('chat', 'exclusions', {}, [qkey]);
  const d: any = q.data;
  const summary: any[] = d?.summary || [];

  return (
    <div className="flex flex-col gap-4">
      <Section title="Who is excluded"
        right={<Btn small onClick={list.reload}>Refresh</Btn>}>
        <Caption>
          The effective list is the union of the shared analytics exclusion editor and
          this page's own built-in addresses. Only rows marked editable can be changed
          from the shared editor; the built-ins are compiled in.
        </Caption>
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading ? <Loading /> : (
          <>
            <KPIRow>
              <KPI label="Effective" value={num((list.data?.effective || []).length)} />
              <KPI label="From shared list" value={num((list.data?.shared || []).length)} />
              <KPI label="Chat built-in" value={num((list.data?.builtin || []).length)} />
            </KPIRow>
            <DataTable rows={list.data?.rows || []} columns={['email', 'source', 'editable']}
              csvName="chat_exclusion_list" empty="Nobody is excluded." maxHeight="20rem" />
          </>
        )}
      </Section>

      <Section title="Excluded users — sessions">
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading ? <Loading /> : d && (
          <>
            <Caption>{d.note}</Caption>
            <KPIRow>
              <KPI label="Excluded sessions" value={num(d.total)} />
              <KPI label="Shown" value={num(d.returned)} hint={d.truncated ? 'server cap hit' : undefined} />
              <KPI label="Accounts" value={num(summary.length)} />
            </KPIRow>
            {d.truncated && (
              <Msg kind="error">
                Only {num(d.returned)} of {num(d.total)} excluded sessions are listed — narrow
                the date range to see the rest.
              </Msg>
            )}
            <div className="flex flex-col gap-2">
              <div className="text-xs uppercase tracking-wide text-grayscale-1100">Sessions per account</div>
              <BarList items={summary.map(s => ({ label: s.email || '(no email)', value: s.sessions }))} />
              {!summary.length && <Caption>No excluded sessions in this date range.</Caption>}
            </div>
            <DataTable rows={d.rows || []} columns={EXCLUDED_COLS} labels={SESSION_LABELS}
              searchable csvName="chat_excluded_sessions"
              empty="No excluded sessions in this date range." maxHeight="30rem" />
            <FullExport action="excluded" params={params} name="chat_excluded_full" />
          </>
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
const PromptCards: React.FC = () => {
  const q = useAdminGet('chat', 'prompt_catalog', {}, []);
  const [open, setOpen] = useState(false);
  const d: any = q.data;
  const counts: any = d?.counts || {};
  const rows = useMemo(() => {
    if (!d) return [];
    const out: { bucket: string; question: string }[] = [];
    (['solve', 'benchmark', 'landscape'] as const).forEach(b => {
      (d[b] || []).forEach((question: string) =>
        out.push({ bucket: b.charAt(0).toUpperCase() + b.slice(1), question }));
    });
    return out;
  }, [d]);

  return (
    <Section title="Landing prompt-card catalogue"
      right={<Btn small onClick={() => setOpen(o => !o)}>{open ? 'Hide catalogue' : 'Show catalogue'}</Btn>}>
      <Caption>
        These are the literal questions the landing page offers. A session is bucketed by
        matching its first user message against them; anything that matches none of them
        is Custom. Match rule: {d?.match_rule || '…'}.
      </Caption>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Solve" value={num(counts.solve)} />
            <KPI label="Benchmark" value={num(counts.benchmark)} />
            <KPI label="Landscape" value={num(counts.landscape)} />
            <KPI label="Total cards" value={num(rows.length)} />
          </KPIRow>
          {open && (
            <DataTable rows={rows} columns={['bucket', 'question']} searchable
              csvName="chat_prompt_catalog" empty="The catalogue is empty."
              maxHeight="34rem" />
          )}
        </>
      )}
    </Section>
  );
};

export default ChatStatsPanel;
